/**
 * MoveSessionModal - Modal for moving a session to a different class
 */

import { useState, useEffect } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Session } from '@/types/session';
import { ClassWithMetrics } from '@/types/class';
import { classesService } from '@/services/classes';

interface MoveSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session;
  onMove: (classId: string | null) => Promise<void>;
}

export function MoveSessionModal({ isOpen, onClose, session, onMove }: MoveSessionModalProps) {
  const [classes, setClasses] = useState<ClassWithMetrics[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(session.classId || null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadClasses();
      setSelectedClassId(session.classId || null);
      setError('');
    }
  }, [isOpen, session.classId]);

  const loadClasses = async () => {
    try {
      setIsLoading(true);
      const fetchedClasses = await classesService.listClasses();
      setClasses(fetchedClasses);
    } catch (err: any) {
      setError(err.message || 'Failed to load classes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      await onMove(selectedClassId);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to move session');
    } finally {
      setIsSubmitting(false);
    }
  };

  const footer = (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={onClose}
        disabled={isSubmitting}
        className="border-[#12356B] text-[#12356B] hover:bg-[#E2E5EC] hover:text-[#12356B]"
      >
        Cancel
      </Button>
      <Button
        type="submit"
        form="move-session-form"
        disabled={isSubmitting || selectedClassId === session.classId}
        className="bg-[#367A25] text-white hover:bg-[#2d6820]"
      >
        {isSubmitting ? 'Moving...' : 'Move Session'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Move Session to Class"
      footer={footer}
    >
      <form id="move-session-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Session info */}
        <div className="bg-[#E2E5EC]/50 rounded-lg p-4 border border-[#CED4DE]">
          <p className="text-sm font-medium text-slate-700">Session:</p>
          <p className="text-base font-semibold text-[#12356B]">{session.sessionName || session.code}</p>
          <p className="text-xs text-slate-500 mt-1">Code: {session.code}</p>
        </div>

        {/* Class selector */}
        <div>
          <label htmlFor="target-class" className="block text-sm font-medium text-slate-700 mb-2">
            Move to:
          </label>

          {isLoading ? (
            <div className="text-sm text-slate-500">Loading classes...</div>
          ) : (
            <select
              id="target-class"
              value={selectedClassId || ''}
              onChange={(e) => setSelectedClassId(e.target.value || null)}
              className="w-full px-3 py-2 border border-[#CED4DE] rounded-md focus:outline-none focus:ring-2 focus:ring-[#12356B]/30 focus:border-[#12356B]"
            >
              <option value="">Uncategorized</option>
              {classes.map((classItem) => (
                <option key={classItem.classId} value={classItem.classId}>
                  {classItem.className} ({classItem.sessionCount} sessions)
                </option>
              ))}
            </select>
          )}

          <p className="text-xs text-slate-500 mt-2">
            {selectedClassId
              ? 'This session will be moved to the selected class'
              : 'This session will be moved to Uncategorized'}
          </p>
        </div>

        {/* Current location indicator */}
        {session.classId && (
          <div className="text-xs text-[#12356B] bg-[#E2E5EC] border border-[#CED4DE] rounded px-3 py-2">
            Currently in:{' '}
            <span className="font-medium">
              {classes.find((c) => c.classId === session.classId)?.className || 'Unknown'}
            </span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="text-sm text-[#c4562a] bg-[#DD6832]/10 border border-[#DD6832]/30 rounded px-3 py-2">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
